import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { EMPTY_STRING } from '../../../../constants/common'
import { useCopyToClipboard, useNotification } from '../../../../hooks'
import { htmlToPlainText } from '../../../../utils/html'
import {
  AI_TEXT_GENERATION_COPY_RESET_TIMEOUT,
  AITextGenerationApplyAction,
  AITextGenerationFieldMode,
} from '../../AITextGenerationField.constants'
import {
  getAITextGenerationNoticeHtml,
  isAIActionInsertBelow,
} from '../../AITextGenerationField.utils'

export function useAITextGenerationFieldState({
  fieldName,
  regenerateRef,
  subjectFieldName,
  onApplyText,
}) {
  const { t } = useTranslation()

  const { setFieldValue, values } = useFormikContext()

  const { handleAddSuccessNotification } = useNotification()

  const [isCopied, setIsCopied] = useState(false)
  const [mode, setMode] = useState(AITextGenerationFieldMode.Field)
  const [isGenerating, setIsGenerating] = useState(false)
  const [draftText, setDraftText] = useState(null)
  const [draftSubject, setDraftSubject] = useState(EMPTY_STRING)
  const [promptText, setPromptText] = useState(EMPTY_STRING)
  const [showAiNotice, setShowAiNotice] = useState(false)

  const copyTimerRef = useRef(null)

  function handleCopySuccess() {
    handleAddSuccessNotification('AITextGeneration.CopiedNotificationKey')
    setIsCopied(true)
    clearTimeout(copyTimerRef.current)
    copyTimerRef.current = setTimeout(() => {
      setIsCopied(false)
    }, AI_TEXT_GENERATION_COPY_RESET_TIMEOUT)
  }

  const [, copyToClipboard] = useCopyToClipboard({
    onSuccess: handleCopySuccess,
  })

  const fieldValue = values[fieldName]

  function applyValue(generatedText, action) {
    const noticeHtml = getAITextGenerationNoticeHtml({
      noticeText: t('AITextGeneration.GeneratedByAINoticeKey'),
      showAiNotice,
    })
    const textToApply = `${generatedText}${noticeHtml}`
    const currentValue = fieldValue || EMPTY_STRING

    const nextValue = onApplyText
      ? onApplyText(textToApply, currentValue, action)
      : isAIActionInsertBelow(action)
        ? `${currentValue}${textToApply}`
        : textToApply

    setFieldValue(fieldName, nextValue)

    // Insert below keeps everything the user wrote, the subject included
    if (subjectFieldName && draftSubject && !isAIActionInsertBelow(action)) {
      setFieldValue(subjectFieldName, draftSubject)
    }
  }

  function handleGenerateStart() {
    setIsGenerating(true)
    setDraftText(null)
    setDraftSubject(EMPTY_STRING)
    setMode(AITextGenerationFieldMode.Draft)
  }

  function handleGenerateStop() {
    setIsGenerating(false)
    setDraftText(null)
    setDraftSubject(EMPTY_STRING)
    setShowAiNotice(false)
    setMode(AITextGenerationFieldMode.Field)
  }

  function handleGenerate(text, prompt, showNotice, subject) {
    setDraftText(text)
    setDraftSubject(subject || EMPTY_STRING)
    setPromptText(prompt)
    setShowAiNotice(Boolean(showNotice))
    setIsGenerating(false)
    setMode(AITextGenerationFieldMode.Draft)
  }

  function handleApply() {
    applyValue(draftText, AITextGenerationApplyAction.Apply)
    setMode(AITextGenerationFieldMode.Field)
  }

  function handleReplace() {
    applyValue(draftText, AITextGenerationApplyAction.Replace)
    setMode(AITextGenerationFieldMode.Field)
  }

  function handleInsertBelow() {
    applyValue(draftText, AITextGenerationApplyAction.InsertBelow)
    setMode(AITextGenerationFieldMode.Field)
  }

  function handleDiscard() {
    setDraftText(null)
    setDraftSubject(EMPTY_STRING)
    setMode(AITextGenerationFieldMode.Field)
  }

  function handleCopy() {
    copyToClipboard(htmlToPlainText(draftText), { html: draftText })
  }

  function handleRegenerate() {
    regenerateRef?.current?.()
  }

  return {
    draftSubject,
    draftText,
    handleApply,
    handleCopy,
    handleDiscard,
    handleGenerate,
    handleGenerateStart,
    handleGenerateStop,
    handleInsertBelow,
    handleRegenerate,
    handleReplace,
    isCopied,
    isGenerating,
    mode,
    promptText,
    showAiNotice,
  }
}
